const INIT_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid', 'rdt_cid', 'oppref'];
const COOKIE_NAME = 'chatlab';
const TTL_DAYS = 90;
const ENCRYPTION_SECRET = 'f0355a4af58bbb5b67f6f949e2873376d7fb180f0d02138d425ddfc8f9ee864f';

async function importKey(secret) {
    const encoder = new TextEncoder();
    const keyMaterial = encoder.encode(secret);
    const hash = await crypto.subtle.digest('SHA-256', keyMaterial);
    return crypto.subtle.importKey('raw', hash, {name: 'AES-GCM', length: 256}, false, ['encrypt', 'decrypt']);
}

function arrayBufferToHex(buffer) {
    return Array.from(new Uint8Array(buffer)).map(b => b.toString(16).padStart(2, '0')).join('');
}

function hexToArrayBuffer(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
        bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes.buffer;
}

async function encryptData(data, debugLogs = false) {
    try {
        const key = await importKey(ENCRYPTION_SECRET);
        const iv = crypto.getRandomValues(new Uint8Array(12));
        const encoder = new TextEncoder();
        const dataJson = JSON.stringify(data);
        const encrypted = await crypto.subtle.encrypt({name: 'AES-GCM', iv: iv}, key, encoder.encode(dataJson));
        return arrayBufferToHex(iv) + ':' + arrayBufferToHex(encrypted);
    } catch (e) {
        if (debugLogs) {
            console.error('[SITE INIT] Encryption failed:', e);
        }
        return null;
    }
}

export async function decryptData(encryptedStr, debugLogs = false) {
    try {
        const [ivHex, encryptedHex] = encryptedStr.split(':');
        if (!ivHex || !encryptedHex) return null;

        const key = await importKey(ENCRYPTION_SECRET);
        const iv = new Uint8Array(hexToArrayBuffer(ivHex));
        const encrypted = hexToArrayBuffer(encryptedHex);
        const decrypted = await crypto.subtle.decrypt({name: 'AES-GCM', iv: iv}, key, encrypted);
        const decoder = new TextDecoder();
        return JSON.parse(decoder.decode(decrypted));
    } catch (e) {
        if (debugLogs) {
            console.error('[SITE INIT] Decryption failed:', e);
        }
        return null;
    }
}

export const setCookie = (name, value, days) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const domainPart = isLocalhost ? '' : ';domain=.chatlab.com';
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/${domainPart};SameSite=Lax`;
};

export const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return decodeURIComponent(parts.pop().split(';').shift());
    }
    return null;
};

export const siteInit = async () => {
    try {
        let urlParams = new URLSearchParams(window.location.search);

        if (!window.location.search && window.location.hash && window.location.hash.includes('?')) {
            const hashParams = window.location.hash.split('?')[1];
            urlParams = new URLSearchParams(hashParams);
        }

        const debugLogsEnabled = urlParams.get('iv') === 'test';

        const utmData = {};
        let hasUtm = false;

        INIT_PARAMS.forEach(param => {
            const value = urlParams.get(param);
            if (value) {
                utmData[param] = value;
                hasUtm = true;
            }
        });

        const existingCookie = getCookie(COOKIE_NAME);
        let existingData = null;

        if (existingCookie && existingCookie.startsWith('timestamp=')) {
            const encryptedData = existingCookie.substring('timestamp='.length);
            existingData = await decryptData(encryptedData, debugLogsEnabled);
        }

        if (debugLogsEnabled || hasUtm) {
            const stored = {
                params: hasUtm ? utmData : (existingData?.params || {}),
                timestamp: Date.now(),
                expiresAt: Date.now() + (TTL_DAYS * 24 * 60 * 60 * 1000),
                debugLogs: debugLogsEnabled || (existingData?.debugLogs || false)
            };

            if (stored.debugLogs) {
                console.log('[SITE INIT] Debug mode enabled');
                console.log('[SITE INIT] URL params found:', utmData);
                console.log('[SITE INIT] Stored data:', stored);
            }

            const encrypted = await encryptData(stored, stored.debugLogs);
            if (encrypted) {
                const cookieValue = `timestamp=${encrypted}`;
                setCookie(COOKIE_NAME, cookieValue, TTL_DAYS);
                if (stored.debugLogs) {
                    console.log('[SITE INIT] Cookie updated (encrypted)');
                }
            }
        }

        const utmParams = hasUtm ? utmData : (existingData?.params || {});
        if (Object.keys(utmParams).length > 0) {
            const source = utmParams.utm_source || (utmParams.gclid ? 'google' : undefined);
            const medium = utmParams.utm_medium || (utmParams.gclid ? 'cpc' : undefined);
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                chatlab_utm_source: source,
                chatlab_utm_medium: medium,
                chatlab_utm_campaign: utmParams.utm_campaign,
                chatlab_utm_content: utmParams.utm_content,
                chatlab_utm_term: utmParams.utm_term,
                chatlab_gclid: utmParams.gclid,
                chatlab_rdt_cid: utmParams.rdt_cid,
                chatlab_oppref: utmParams.oppref,
            });
        }
    } catch (e) {
        console.error('Failed to capture INIT params:', e);
    }
};

